/** Draft advisory preparation. No publication, reporting or applicability is inferred. */
import type { VulnerabilityEventRow } from '../../lib/types';
import advisoryTemplateMarkdown from './cra-advisory-template.md?raw';
import customerNoticeMarkdown from './customer-notice-template.md?raw';

export const MAX_ADVISORY_LENGTH = 200_000;

function known(value: string | null): string | undefined {
  return value?.trim() || undefined;
}

function distinct(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))];
}

function scoreFacts(value: number | string | null): Record<string, string> {
  if (value === null || (typeof value === 'string' && !value.trim())) return {};
  const score = typeof value === 'number' ? value : Number(value.trim().replace(',', '.'));
  if (!Number.isFinite(score) || score < 0 || score > 10) return {};
  const severity = score >= 9 ? 'critical' : score >= 7 ? 'high' : score >= 4 ? 'medium' : score > 0 ? 'low' : 'none';
  return { cvss_base_score: String(score), severity, severity_uppercase: severity.toUpperCase() };
}

/** Only substitute facts the event actually supplies. Unknown placeholders survive. */
function eventFacts(event: VulnerabilityEventRow): Record<string, string | undefined> {
  const instanceProducts = distinct(event.betroffene.map((instance) => instance.produkttyp));
  const products = instanceProducts.length ? instanceProducts : distinct(event.produktversionen.map((version) => version.name));
  const productVersions = distinct([
    ...event.produktversionen.map((version) => [known(version.name), known(version.version_nr)].filter(Boolean).join(' · ')),
    ...event.betroffene.map((instance) => [known(instance.produkttyp), known(instance.produktversion)].filter(Boolean).join(' · ')),
  ]);
  const component = known(event.komponente.name);
  const cve = known(event.cve.id);
  const identity = products.join(', ') || component;
  const title = identity ? `${identity}: security advisory${cve ? ` (${cve})` : ''}` : cve ? `Security advisory: ${cve}` : undefined;
  const sources = distinct(event.kev.sources);
  const active = event.cve.aktiv_ausgenutzt;
  return {
    advisory_title: title,
    advisory_state: 'draft',
    assessment_reason: known(event.begruendung),
    cve_id: cve,
    summary: known(event.cve.beschreibung) ?? known(event.kev.beschreibung),
    component_name: component,
    component_version: known(event.komponente.version),
    component_manufacturer: known(event.komponente.hersteller),
    manufacturer: known(event.komponente.hersteller),
    product_name: products.length ? products.join(', ') : undefined,
    product_versions: productVersions.length ? productVersions.join('; ') : undefined,
    remediation_action: known(event.kev.empfohlene_massnahme),
    exploitation_status: active ? 'exploited-in-the-wild' : undefined,
    exploitation_details: active
      ? `The vulnerability is flagged as actively exploited${sources.length ? ` by ${sources.join(', ')}` : ' in the available vulnerability data'}. Applicability and any exploitation in our products require separate assessment. This draft does not establish that regulatory reporting has occurred.`
      : undefined,
    ...scoreFacts(event.cve.base_score),
  };
}

/**
 * Reference downloads can contain frontmatter, instructions, an e-mail and a
 * fictional example. Import only their first actual fenced Markdown advisory.
 * Ordinary custom Markdown remains unchanged apart from known placeholders.
 */
function advisoryBody(source: string): string {
  const text = source.replace(/^\uFEFF/, '').trim();
  if (!text) throw new Error('Die Vorlage ist leer. Bitte wählen Sie eine Markdown- oder Textdatei mit Inhalt.');
  if (text.length > MAX_ADVISORY_LENGTH) throw new Error('Die Vorlage ist zu lang. Maximal 200.000 Zeichen sind erlaubt.');
  if (text.includes('\0')) throw new Error('Die Vorlage enthält ungültige Zeichen. Bitte verwenden Sie eine Markdown- oder Textdatei.');
  const fences = /(?:^|\n)[ \t]{0,3}(`{3,}|~{3,})[ \t]*(?:markdown|md)[ \t]*\r?\n([\s\S]*?)\r?\n[ \t]{0,3}\1[ \t]*(?=\r?\n|$)/gi;
  for (const match of text.matchAll(fences)) {
    const body = match[2] ?? '';
    if (/(?:^|\n)#\s/.test(body) && /\{\{\s*(?:advisory_title|cve_id)\s*\}\}|\*\*CVE[ -]ID\*\*/i.test(body)) return body.trim();
  }
  return text;
}

export function importAdvisoryTemplate(source: string, event: VulnerabilityEventRow): string {
  const facts = eventFacts(event);
  // A callback prevents "$&" substitution and a single pass keeps inserted
  // placeholders literal instead of interpreting event descriptions as a template.
  const draft = advisoryBody(source).replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (placeholder, key: string) => {
    const name = key.toLowerCase();
    return Object.hasOwn(facts, name) ? facts[name] ?? placeholder : placeholder;
  });
  if (draft.length > MAX_ADVISORY_LENGTH) throw new Error('Der ausgefüllte Entwurf ist zu lang. Maximal 200.000 Zeichen sind erlaubt.');
  return draft;
}

export function buildAdvisory(event: VulnerabilityEventRow): string {
  return importAdvisoryTemplate(DEFAULT_ADVISORY_TEMPLATE, event);
}

/**
 * The short customer notice: what used to be the built-in mail text. Greeting,
 * the customer's instances and the closing are added per customer when the
 * drafts are built; its first heading becomes the subject.
 */
export function buildCustomerNotice(event: VulnerabilityEventRow): string {
  return importAdvisoryTemplate(customerNoticeMarkdown, event);
}

/**
 * Use the edited document as the assessment reason without inventing a summary.
 * Remove completed HTML guidance comments, retaining literal comments in fenced
 * or inline code. Incomplete comments stay visible so no following text is lost.
 */
export function deriveAssessmentReason(content: string): string {
  const source = content.replace(/\r\n?/g, '\n');
  const tokens = /<!--[\s\S]*?-->|^ {0,3}(`{3,}|~{3,})[^\n]*(?:\n|$)|`+/gm;
  let result = '';
  let text = '';
  let offset = 0;
  function flushText(): void {
    result += text.replace(/\n(?:[ \t]*\n){2,}/g, '\n\n');
    text = '';
  }
  let token: RegExpExecArray | null;
  while ((token = tokens.exec(source)) !== null) {
    text += source.slice(offset, token.index);
    offset = tokens.lastIndex;
    if (token[0].startsWith('<!--')) continue;

    const fence = token[1];
    let codeEnd: number | undefined;
    if (fence) {
      const closing = new RegExp(`^ {0,3}${fence[0]}{${fence.length},}[ \\t]*(?:\\n|$)`, 'gm');
      closing.lastIndex = offset;
      codeEnd = closing.exec(source) ? closing.lastIndex : source.length;
    } else {
      const closing = /`+/g;
      closing.lastIndex = offset;
      let candidate: RegExpExecArray | null;
      while ((candidate = closing.exec(source)) !== null) {
        if (candidate[0].length === token[0].length) { codeEnd = closing.lastIndex; break; }
      }
    }
    if (codeEnd === undefined) {
      text += token[0];
      continue;
    }
    flushText();
    result += source.slice(token.index, codeEnd);
    offset = codeEnd;
    tokens.lastIndex = codeEnd;
  }
  text += source.slice(offset);
  flushText();
  return result.trim();
}

/** The downloadable reference template doubles as the built-in default. */
export const DEFAULT_ADVISORY_TEMPLATE = advisoryTemplateMarkdown;
